// WalletRegister.tsx — the wallet register (operator, 6 Oct 2026): every
// address the books know, across every party, and what each one is — in use,
// held by screening, retired (still watched) or archived (not watched).
//
// His rulings (db/0064): archive stops the chain reader, KYT and Energy
// reading the address; nothing is ever deleted; nothing retired comes back. So
// the two steps here — Retire, Archive — go one way, each asks for a reason,
// and each says what it does before it is taken. An address is added through
// the same door Party detail uses (POST /api/parties/:slug/wallets). An
// address that follows from a Ledger account key is that key's scan's: shown,
// never retired here. The wallet system's class groups land here when it
// merges (docs/MERGE-PLAN.md, D5).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type RegisterWallet } from '../lib/api';
import { errorWords, readWords } from '../lib/errors';
import { fmtDay, fmtRelative } from '../lib/format';
import { tg } from '../lib/telegram';
import { WALLET_CHAINS, stepWords, walletState, watchWords } from '../lib/walletStates';
import { ScreenHeader } from '../components/ScreenHeader';
import { Section } from '../components/Section';
import { Pill } from '../components/Pill';
import { Field } from '../components/Field';
import { ErrorLine } from '../components/ErrorLine';
import { Pending } from '../components/Pending';
import { EmptyState, StateLine } from '../components/kit';
import { Combobox, type ComboboxOption } from '../components/Combobox';
import { CopyRef } from '../components/CopyRef';
import { treasuryHref } from '../lib/ownAccounts';
import { deskAccounts } from '../lib/desk-wallet';
import { WaitingLine, waitingOf } from '../components/v2/wallets/Waiting';

type Show = 'current' | 'retired' | 'archived' | 'all';
const SHOWS: Array<{ key: Show; label: string; has: (w: RegisterWallet) => boolean }> = [
  { key: 'current',  label: 'In use',   has: (w) => w.status === 'active' || w.status === 'flagged' },
  { key: 'retired',  label: 'Retired',  has: (w) => w.status === 'archived' },
  { key: 'archived', label: 'Archived', has: (w) => w.status === 'unwatched' },
  { key: 'all',      label: 'All',      has: () => true },
];

export function WalletRegister() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['wallets', 'register'], queryFn: () => api.walletRegister() });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const canChange = me.data?.role === 'full';
  // The server decides (owner only); the screen follows what it says, and offers it when it says nothing.
  const canApprovePayouts = me.data?.canApprovePayouts !== false;
  const [show, setShow] = useState<Show>('current');
  const [chain, setChain] = useState<string>('');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  // What the last step reached beyond the Books — said here, not on its row:
  // an archived row leaves the "In use" list the moment it is saved.
  const [notice, setNotice] = useState<string | null>(null);
  // The row a tap on the waiting line went to: its id and a count, so a second tap on the same one scrolls again.
  const [focus, setFocus] = useState<{ id: string; n: number } | null>(null);

  const all = q.data?.wallets ?? [];
  const waiting = useMemo(() => waitingOf(all), [all]);
  // ?open=<id> (Custody's Needs you, 4 Nov 2026): to that row, its approve step open, once the register is read.
  const [params, setParams] = useSearchParams();
  const openParam = params.get('open');
  useEffect(() => {
    if (!openParam || !q.data) return;
    if (all.some((w) => w.id === openParam)) { setShow('current'); setChain(''); setSearch(''); setFocus({ id: openParam, n: 1 }); }
    const next = new URLSearchParams(params);
    next.delete('open');
    setParams(next, { replace: true });
  }, [openParam, !!q.data]);   // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => Object.fromEntries(SHOWS.map((s) => [s.key, all.filter(s.has).length])) as Record<Show, number>, [all]);
  const shown = useMemo(() => {
    const has = SHOWS.find((s) => s.key === show)!.has;
    const needle = search.trim().toLowerCase();
    return all.filter((w) => has(w) && (!chain || w.chain === chain) && (!needle
      || w.address.toLowerCase().includes(needle)
      || (w.label ?? '').toLowerCase().includes(needle)
      || (w.party_name ?? w.party_slug).toLowerCase().includes(needle)));
  }, [all, show, chain, search]);
  // One section per party, in the register's order: desk wallets first.
  const parties = useMemo(() => {
    const out: Array<{ slug: string; name: string; desk: boolean; treasury: string | null; type: string; rows: RegisterWallet[] }> = [];
    for (const w of shown) {
      let g = out.find((x) => x.slug === w.party_slug);
      if (!g) { g = { slug: w.party_slug, name: w.party_name ?? w.party_slug, desk: !!w.treasury_slug, treasury: w.treasury_slug, type: w.party_type, rows: [] }; out.push(g); }
      g.rows.push(w);
    }
    return out;
  }, [shown]);
  const watchedCount = all.filter((w) => w.watched).length;

  return (
    <div className="space-y-4">
      <ScreenHeader
        title="Wallet register"
        // A failed refresh keeps the old data beside the error: its counts are not claimed (Astra, 22 Oct).
        subtitle={q.isError
          ? 'not read'
          : q.data
            ? <>{all.length} addresses · <span className="font-mono tabular">{watchedCount}</span> watched by the chain reader</>
            : 'not read yet'}
        actions={
          <>
            {canChange && (
              <button type="button" className="btn-secondary text-body py-1.5" onClick={() => setAdding((a) => !a)}>
                {adding ? 'Cancel' : '+ Add wallet'}
              </button>
            )}
            <button type="button" className="btn-secondary text-body py-1.5 disabled:opacity-50" disabled={q.isFetching}
              onClick={() => qc.invalidateQueries({ queryKey: ['wallets'] })}>
              {q.isFetching ? 'Reading…' : 'Refresh'}
            </button>
          </>
        }
        help={
          <>
            <p>Every wallet address the books know, for every party. An address is <b>in use</b> while the desk hands it out.</p>
            <p><b>Retire</b> an address when the desk stops handing it out. The chain reader keeps reading it, so money a customer
              sends there months later is still seen. The energy desk stops serving it.</p>
            <p><b>Archive</b> an address nobody will pay into again. The chain reader stops reading it and the KYT and Energy screens
              stop counting it, so money sent there afterwards is not seen in the Books. The KYT screening worker is told to stop
              screening it, and the energy desk to stop serving it.</p>
            <p>Both steps go one way and ask for a reason, which goes into the audit trail. Nothing is deleted, and nothing retired comes back.
              An address that comes from the Ledger account key belongs to that key and is never retired by hand.</p>
          </>
        }
      />

      {/* WAITING FOR HIS APPROVAL, first (4 Nov 2026): a tap shows every row, scrolls to that one and opens its approve step. */}
      {q.data && !q.isError && waiting.length > 0 && (
        <div className="rounded-lg border border-warn/25 bg-warn-bg px-3 py-2 text-body text-warn">
          <WaitingLine waiting={waiting} onOpen={(w) => { setShow('current'); setChain(''); setSearch(''); setFocus((f) => ({ id: w.id, n: (f?.n ?? 0) + 1 })); }} />
        </div>
      )}

      {notice && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-tg-secondary px-3 py-2 text-body">
          <span>{notice}</span>
          <button type="button" className="btn-link text-meta shrink-0" onClick={() => setNotice(null)}>dismiss</button>
        </div>
      )}

      {adding && <AddWallet onDone={() => { setAdding(false); qc.invalidateQueries({ queryKey: ['wallets'] }); }} wallets={all} />}

      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {SHOWS.map((s) => (
            <button key={s.key} type="button" onClick={() => setShow(s.key)} className={`seg text-meta whitespace-nowrap ${show === s.key ? 'seg-on' : ''}`}>
              {s.label} <span className="font-mono tabular opacity-70">{q.data && !q.isError ? counts[s.key] : '—'}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          {['', ...WALLET_CHAINS].filter((c) => !c || all.some((w) => w.chain === c)).map((c) => (
            <button key={c || 'any'} type="button" onClick={() => setChain(c)} className={`seg text-meta whitespace-nowrap max-lg:px-2 ${chain === c ? 'seg-on' : ''}`}>
              {c || 'Every chain'}
            </button>
          ))}
        </div>
        <input className="input w-full text-field lg:text-body" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search party, label or address" />
      </div>

      {q.isLoading && <Pending what="Reading the register…" />}
      {q.isError && <ReadFailed error={q.error} onRetry={() => q.refetch()} />}
      {q.data && !q.isError && parties.length === 0 && <EmptyState>Nothing here with these filters.</EmptyState>}

      {parties.map((g) => {
        const byHand = g.rows.filter((w) => !w.from_ledger_key);
        const fromKey = g.rows.filter((w) => w.from_ledger_key);
        return (
          <Section key={g.slug} title={g.name}
            accessory={g.desk
              // A desk wallet is read on Treasury, opened (operator, 21 Oct 2026) — its party page had nothing on it.
              ? <Link to={treasuryHref(g.treasury)} className="btn-link text-meta whitespace-nowrap">on Wallets →</Link>
              : <Link to={`/parties/${encodeURIComponent(g.slug)}`} className="btn-link text-meta whitespace-nowrap">{g.type.replace('_', ' ')} →</Link>}>
            {byHand.map((w) => <RegisterRow key={w.id} w={w} canChange={canChange} canApprovePayouts={canApprovePayouts} onNotice={setNotice} focus={focus?.id === w.id ? focus.n : 0} />)}
            {fromKey.length > 0 && (
              <details className={byHand.length ? 'pt-2' : ''}>
                <summary className="text-meta text-tg-hint cursor-pointer">
                  {fromKey.length} address{fromKey.length === 1 ? '' : 'es'} from the Ledger account key — the key's scan keeps them
                </summary>
                <div className="pt-1">
                  {/* The key's own state — it used to stand only on the desk account's party page, which now opens Treasury. */}
                  {g.desk && <LedgerKeyLines slug={g.slug} />}
                  {fromKey.map((w) => <RegisterRow key={w.id} w={w} canChange={false} canApprovePayouts={false} onNotice={setNotice} />)}
                </div>
              </details>
            )}
          </Section>
        );
      })}
    </div>
  );
}

/** WHAT THE LEDGER ACCOUNT KEY IS DOING — one line per key: the next receive address and when its branches were last
 *  scanned. The state came from the desk account's party page, the only place that said it, and that page now opens
 *  Treasury (operator, 21 Oct 2026). The key itself never reaches a screen — the server says only that it exists. The party
 *  read is the one Book trade and the Last 10 lists already make, under the same key. */
function LedgerKeyLines({ slug }: { slug: string }) {
  const q = useQuery({
    queryKey: ['party', slug, 'balances'],
    queryFn: () => api.partyDetail(slug, 'dubaiotc'),
    staleTime: 60_000,
  });
  if (q.isError) return <p className="py-1 text-meta text-tg-hint">Couldn&rsquo;t read the key&rsquo;s scan — {readWords(q.error)}</p>;
  const keys = q.data?.wallet_keys ?? [];
  if (keys.length === 0) return null;
  return (
    <>
      {keys.map((k, i) => (
        <p key={i} className="py-1 text-meta text-tg-hint">
          Key scan · next receive 0/{k.last_receive_used + 1}
          {' · '}{k.last_scanned_at ? `scanned ${fmtRelative(k.last_scanned_at)}` : 'not scanned yet'}
        </p>
      ))}
    </>
  );
}

function RegisterRow({ w, canChange, canApprovePayouts, onNotice, focus = 0 }: { w: RegisterWallet; canChange: boolean; canApprovePayouts: boolean; onNotice: (words: string) => void; focus?: number }) {
  const qc = useQueryClient();
  const state = walletState(w.status);
  const [step, setStep] = useState<'retire' | 'archive' | 'label' | 'approve' | null>(null);
  const [reason, setReason] = useState('');
  const [label, setLabel] = useState(w.label ?? '');
  const change = useMutation({
    // Run from the label, retire and archive steps only; approve has its own call (below).
    mutationFn: () => api.changeWallet(w.id, step === 'label' ? { action: 'label', label } : { action: step as 'retire' | 'archive', reason }),
    onSuccess: (r) => {
      tg()?.HapticFeedback.notificationOccurred('success');
      const name = `${w.chain}${w.label ? ` · ${w.label}` : ''}`;
      // What the energy desk did (Energy's feed, read by routes/wallets.ts energyWord).
      const e = r.energy;
      const energyWords = !e ? ''
        : !e.told ? ` The energy desk could not be told (${e.reason}) — the ten-minute check tries again.`
        : 'to' in e ? ` The energy desk ${e.to === 'retired' ? 'retired it too' : 'paused it'}.`
        : ' The energy desk had nothing to change for it.';
      if (step === 'retire') onNotice(`${name} is retired — no longer handed out; ${watchWords('archived', w.chain)}.${energyWords}`);
      if (step === 'archive') {
        // The screening worker's own list (KYT's sentences, 6 Oct 2026).
        const k = r.kyt;
        const kytWords = !k ? ''
          : k.told ? (k.removed ? ' KYT stopped screening it.' : ' KYT was not screening it.')
          // With the chain, always: the address alone matches an EVM key's twin
          // on another network, and the worker then removes nothing and asks
          // (KYT, 63ccaea) — the chain names exactly one wallet.
          : ` KYT could not be told (${k.reason}) — send /unwatch ${w.chain} ${w.address} in the ops group.`;
        onNotice(`${name} is archived — the chain reader no longer reads it.${kytWords}${energyWords}`);
      }
      setStep(null); setReason('');
      qc.invalidateQueries({ queryKey: ['wallets'] });
      qc.invalidateQueries({ queryKey: ['party', w.party_slug] });
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });
  // A payout address is payable by the wallet only once the Books approve it (N-061, routes/wallets.ts): the owner
  // alone, from his enrolled device through Cloudflare Access — the server says so in words when it is not.
  const approve = useMutation({
    mutationFn: () => api.approvePayoutAddress(w.id),
    onSuccess: (r) => {
      tg()?.HapticFeedback.notificationOccurred('success');
      const name = `${w.chain}${w.label ? ` · ${w.label}` : ''}`;
      onNotice(r.already ? `${name} was already approved for payouts.` : `${name} is approved for payouts.`);
      setStep(null);
      qc.invalidateQueries({ queryKey: ['wallets'] });
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });
  const isPayout = w.class === 'customer_payout';
  // Payable only when the OWNER approved it (the wallet's db/0092); a Books approval by anyone else is his to replace.
  const approved = (w.payout_owner_approved ?? w.payout_approved_via === 'books') && !!w.payout_approved_at;
  // A desk wallet the wallet lists as a payout destination carries a payout stage (item 6, 2 Nov 2026): the owner approves it too.
  const approvable = canChange && (isPayout || (w.payout_stage !== undefined && w.payout_stage !== null)) && w.status === 'active' && !approved && w.payout_approved_via !== 'ledger_message';
  const canRetire = canChange && !w.from_ledger_key && (w.status === 'active' || w.status === 'flagged');
  const canArchive = canChange && !w.from_ledger_key && w.status !== 'unwatched';
  const open = (s: 'retire' | 'archive' | 'label' | 'approve') => { setStep((x) => (x === s ? null : s)); change.reset(); approve.reset(); };
  // Reached from the waiting line at the top: in view, its approve step open.
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focus) return;
    if (approvable) setStep('approve');
    el.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focus]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={el} className="py-1.5 border-b border-tg-secondary last:border-b-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 break-words text-body font-medium">{w.chain}{w.label && ` · ${w.label}`}</span>
        <Pill tone={state.tone} className="shrink-0">{state.word}</Pill>
      </div>
      <CopyRef value={w.address} className="font-mono text-meta text-tg-hint break-all" />
      <div className="text-meta mt-0.5 text-tg-hint">
        Added {fmtDay(w.first_seen_at, true)}
        {w.last_seen_at && <> · last seen {fmtRelative(w.last_seen_at)}</>}
        {w.chain_tx_count != null && <> · <span className="font-mono tabular">{w.chain_tx_count}</span> on chain</>}
        {w.from_ledger_key && w.derivation && <> · <span className="font-mono">{w.derivation}</span></>}
      </div>
      {w.status === 'archived' && w.retired_at && (
        <div className="text-meta mt-0.5 text-tg-hint">Retired {fmtDay(w.retired_at, true)} · {watchWords(w.status, w.chain)}</div>
      )}
      {w.status === 'unwatched' && (
        <div className="text-meta mt-0.5 text-tg-hint">
          {w.retired_at && w.unwatched_at && w.retired_at.slice(0, 10) !== w.unwatched_at.slice(0, 10) && <>Retired {fmtDay(w.retired_at, true)} · </>}
          Archived{w.unwatched_at ? ` ${fmtDay(w.unwatched_at, true)}` : ''} · {watchWords(w.status, w.chain)}
        </div>
      )}
      {w.status === 'flagged' && <div className="text-meta mt-0.5 text-tg-hint">{state.watched}</div>}
      {isPayout && (
        <div className="text-meta mt-0.5 text-tg-hint">
          Payout address · {approved ? `approved ${fmtDay(w.payout_approved_at!, true)}` : w.payout_approved_via === 'books' ? 'needs the owner\'s approval' : 'not approved'}
        </div>
      )}
      {w.status === 'active' && !w.chain_read && <div className="text-meta mt-0.5 text-tg-hint">{watchWords(w.status, w.chain)}</div>}
      {/* A desk address the chain reader does not read while the register says it is in use is worth a line. */}
      {(w.status === 'active' || w.status === 'archived') && w.chain_read && w.treasury_slug && !w.watched && (
        <div className="text-meta mt-0.5 text-tg-hint">Not read by the chain reader — its party is not active</div>
      )}

      {canChange && (
        <div className="flex gap-x-4 pt-1">
          <button type="button" className="btn-link text-meta max-lg:min-h-11 max-lg:px-1" onClick={() => open('label')}>{step === 'label' ? 'cancel' : 'label'}</button>
          {canRetire && <button type="button" className="btn-link text-meta max-lg:min-h-11 max-lg:px-1" onClick={() => open('retire')}>{step === 'retire' ? 'cancel' : 'retire'}</button>}
          {canArchive && <button type="button" className="btn-link text-meta max-lg:min-h-11 max-lg:px-1" onClick={() => open('archive')}>{step === 'archive' ? 'cancel' : 'archive'}</button>}
          {approvable && <button type="button" className="btn-link text-meta max-lg:min-h-11 max-lg:px-1 disabled:opacity-50" disabled={!canApprovePayouts}
            onClick={() => open('approve')}>{step === 'approve' ? 'cancel' : 'approve payout'}</button>}
        </div>
      )}
      {approvable && !canApprovePayouts && <div className="text-meta mt-0.5 text-tg-hint">Only the owner approves payout addresses</div>}

      {step === 'approve' && (
        <div className="space-y-2 pt-1.5">
          <p className="text-meta">Once approved, the wallet can pay this address.</p>
          <button type="button" className="btn-primary w-full text-body py-1.5 disabled:opacity-50"
            disabled={approve.isPending} onClick={() => approve.mutate()}>
            {approve.isPending ? 'Approving…' : 'Approve payout address'}
          </button>
        </div>
      )}

      {step === 'label' && (
        <div className="flex gap-2 pt-1.5">
          <input className="input flex-1 text-field lg:text-body" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Office TRC20 · Nov 2026" />
          <button type="button" className="btn-secondary text-body py-1" disabled={change.isPending} onClick={() => change.mutate()}>
            {change.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      {(step === 'retire' || step === 'archive') && (
        <div className="space-y-2 pt-1.5">
          <p className="text-meta">{stepWords(step, w.chain)}</p>
          <Field label="Why" hint="at least 8 characters">
            <input className="input w-full text-field lg:text-body" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={step === 'retire' ? 'e.g. November rotation' : 'e.g. nobody pays into it any more'} />
          </Field>
          {/* Archive is the step that makes the books blind to an address: red, as the
              reverse door's confirm is (components/ReverseEntry.tsx). */}
          <button type="button" className={`${step === 'archive' ? 'rounded-lg bg-neg text-white px-3 disabled:opacity-40' : 'btn-primary disabled:opacity-50'} w-full text-body py-1.5`}
            disabled={reason.trim().length < 8 || change.isPending} onClick={() => change.mutate()}>
            {change.isPending ? 'Saving…' : step === 'retire' ? 'Retire this address' : 'Archive — stop watching it'}
          </button>
        </div>
      )}
      <ErrorLine>{change.isError ? errorWords(change.error) : approve.isError ? errorWords(approve.error) : null}</ErrorLine>
    </div>
  );
}

function AddWallet({ onDone, wallets }: { onDone: () => void; wallets: RegisterWallet[] }) {
  const partiesQ = useQuery({ queryKey: ['parties', 'all-for-wallets'], queryFn: () => api.listParties({ pageSize: 1000 }) });
  // Desk or Party first (his rule, 4 Nov 2026 — lib/desk-wallet.ts): a desk wallet goes under its group · network, a
  // customer's or a merchant's under them. A folder of desk accounts is offered on neither side.
  const [whose, setWhose] = useState<'desk' | 'party'>('party');
  const [slug, setSlug] = useState('');
  const [pickedChain, setChain] = useState<typeof WALLET_CHAINS[number]>('TRC20');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [retirePrev, setRetirePrev] = useState(false);
  const rows = partiesQ.data?.parties ?? [];
  const desk = useMemo(() => deskAccounts(rows), [partiesQ.data]);   // eslint-disable-line react-hooks/exhaustive-deps
  const options: ComboboxOption[] = useMemo(() => (whose === 'desk'
    ? desk.map((a) => ({ value: a.slug, label: a.name, group: a.chain ? 'Group · network' : 'Other desk accounts' }))
    : rows.filter((p) => p.party_type !== 'own_account')
      .map((p) => ({ value: p.slug, label: p.display_name || p.slug, secondary: p.party_type.replace('_', ' ') }))
      .sort((a, b) => a.label.localeCompare(b.label))), [whose, desk, partiesQ.data]);   // eslint-disable-line react-hooks/exhaustive-deps
  // A group · network fixes the network: "Hot · ERC20" takes ERC20 addresses only.
  const fixed = whose === 'desk' ? (desk.find((a) => a.slug === slug)?.chain ?? null) : null;
  const chain = fixed ?? pickedChain;
  const pick = (w: 'desk' | 'party') => { setWhose(w); setSlug(''); setLabel(''); setRetirePrev(false); };
  // The same count the add door's tick retires: this party's hand-registered addresses in use on this chain.
  const others = wallets.filter((w) => w.party_slug === slug && w.chain === chain && w.status === 'active' && !w.from_ledger_key).length;
  const willRetire = retirePrev && others > 0;
  // What still stops the save, in words: every wallet in the register belongs to a party.
  const missing = !slug ? (whose === 'desk' ? 'Pick the group · network this wallet goes under.' : 'Pick the party this wallet belongs to — search by name above.')
    : !address.trim() ? 'Paste the address.' : null;
  const add = useMutation({
    mutationFn: () => api.addPartyWallet(slug, { chain, address: address.trim(), label: whose === 'party' ? label.trim() || undefined : undefined, retire_previous_on_chain: willRetire }),
    onSuccess: () => { tg()?.HapticFeedback.notificationOccurred('success'); onDone(); },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });
  return (
    <Section title="Add a wallet">
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-1" role="group" aria-label="Whose wallet">
          {(['desk', 'party'] as const).map((w) => (
            <button key={w} type="button" aria-pressed={whose === w} onClick={() => pick(w)} className={`seg text-meta ${whose === w ? 'seg-on' : ''}`}>{w === 'desk' ? 'Desk' : 'Party'}</button>
          ))}
        </div>
        <Field label={whose === 'desk' ? 'Group · network' : 'Party'}>
          <Combobox value={slug} onChange={setSlug} options={options} loading={partiesQ.isLoading} placeholder={whose === 'desk' ? 'Search Hot · ERC20, …' : 'Search party'} />
        </Field>
        {fixed ? (
          <p className="text-meta text-tg-hint">Network <span className="text-tg-text">{fixed}</span></p>
        ) : (
          <div className="grid grid-cols-4 gap-1">
            {WALLET_CHAINS.map((c) => (
              <button key={c} type="button" onClick={() => setChain(c)} className={`seg text-meta ${chain === c ? 'seg-on' : ''}`}>{c}</button>
            ))}
          </div>
        )}
        <Field label="Address">
          <input className="input w-full font-mono text-field lg:text-body" value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        {whose === 'party' && (
          <Field label="Label" hint="optional">
            <input className="input w-full text-field lg:text-body" value={label} onChange={(e) => setLabel(e.target.value)} />
          </Field>
        )}
        {others > 0 && (
          <label className="flex items-center gap-2 text-meta">
            <input type="checkbox" checked={retirePrev} onChange={(e) => setRetirePrev(e.target.checked)} />
            <span className="text-tg-hint">Retire this party&apos;s other {chain} address{others === 1 ? '' : 'es'} in use ({others}) — still watched</span>
          </label>
        )}
        <button type="button" className="btn-primary w-full text-body py-1.5 disabled:opacity-50"
          disabled={!!missing || add.isPending} onClick={() => add.mutate()}>
          {add.isPending ? 'Saving…' : willRetire ? `Save and retire ${others}` : 'Save wallet'}
        </button>
        {/* Why it cannot save yet, said (the operator, 18 Oct 2026: "why not able to save this" — the party was empty
            and the button only dimmed). */}
        {missing && !add.isPending && <p className="text-meta text-tg-hint" data-why-not="">{missing}</p>}
        <ErrorLine>{add.isError ? errorWords(add.error) : null}</ErrorLine>
      </div>
    </Section>
  );
}

/** A read that failed, as a line of its own (the sweep, 21 Oct 2026) — never a register of zeros: what failed, the first sentence of
 *  what the books said, the rest behind a tap, and the way out. */
function ReadFailed({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const words = readWords(error);
  const first = words.split(/(?<=[.!?])\s+/)[0] ?? words;
  return (
    <StateLine
      tone="bad"
      more={words.slice(first.length).trim() || undefined}
      action={<button type="button" className="btn-link" onClick={onRetry}>Try again</button>}
    >
      Couldn&rsquo;t read the register. {first}
    </StateLine>
  );
}
